import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

import "./PostProjectFormPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function PostProjectFormPage() {
  const navigate = useNavigate();

  // ===============================
  // Current user
  // ===============================
  const [currentUser, setCurrentUser] = useState({ fullname: "" });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser({ fullname: user.fullname });
    }
  }, []);

  // ===============================
  // Form state
  // ===============================
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    projectBudget: "", // now matches backend
    postedBy: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Set postedBy after currentUser loads
  useEffect(() => {
    if (currentUser.fullname) {
      setFormData((prev) => ({
        ...prev,
        postedBy: currentUser.fullname,
      }));
    }
  }, [currentUser]);

  // ===============================
  // Handlers
  // ===============================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!imageFile) {
      setError("Please select a project image.");
      setLoading(false);
      return;
    }

    // Validate projectBudget
    const totalBudget = parseFloat(formData.projectBudget);
    if (isNaN(totalBudget) || totalBudget < 0) {
      setError("Please enter a valid budget.");
      setLoading(false);
      return;
    }

    try {
      const form = new FormData();
      form.append("title", formData.title);
      form.append("description", formData.description);
      form.append("projectBudget", totalBudget); // send as number now
      form.append("postedBy", formData.postedBy);
      form.append("image", imageFile);

      await axios.post(`${API_BASE_URL}/projects`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Project posted successfully!");
      navigate(-1);
    } catch (err) {
      console.error("Failed to post project:", err.response?.data || err.message);
      setError(
        err.response?.data?.message || "Failed to post project. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="post-project-page">
      <h1 className="post-project-title">Post a New Project</h1>

      <motion.button
        className="back-btn"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </motion.button>

      <form className="post-project-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            required
          />
        </div>

        <div className="form-group">
          <label>Budget (GHS)</label>
          <input
            type="number"
            name="projectBudget"
            value={formData.projectBudget}
            onChange={handleChange}
            min="0"
            step="0.01"
            required
          />
        </div>

        <div className="form-group">
          <label>Project Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Posted By</label>
          <input
            type="text"
            name="postedBy"
            value={formData.postedBy}
            disabled
          />
        </div>

        {error && <div className="form-error">{error}</div>}

        <motion.button
          type="submit"
          className="post-project-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={loading}
        >
          {loading ? "Posting…" : "Post Project"}
        </motion.button>
      </form>
    </div>
  );
}
